/**
 * Saved preparation profiles (explicit opt-in) and the attempt history under
 * each, read from canonical results. An unmeasured attempt shows as "not
 * measured", never as 0. Nothing here publishes anything: a result reaches a
 * portfolio only through the learner's existing per-item publication.
 */
import {
  useDeletePrepProfileMutation,
  useGetPrepProfileAttemptsQuery,
  useListPrepProfilesQuery,
  type PrepProfile,
} from '../../store/api/interviewPrepApi';
import { useState } from 'react';
import { PrepHistoryList } from './PrepHistoryList';
import { mutationOutcome } from '../../utils/mutationOutcome';

function ProfileHistory({ id }: { id: string }) {
  const { data, isLoading, isError } = useGetPrepProfileAttemptsQuery(id);
  if (isLoading) return <p className="text-xs text-gray-500">Loading history…</p>;
  if (isError || !data) return <p className="text-xs text-gray-500">History is not available right now.</p>;
  return <PrepHistoryList attempts={data.attempts} summary={data.summary} />;
}

interface Props {
  selectedId: string | null;
  onSelect: (profile: PrepProfile | null) => void;
}

export function PrepProfilesPanel({ selectedId, onSelect }: Props) {
  const { data: profiles, isLoading, isError } = useListPrepProfilesQuery();
  const [remove, removeState] = useDeletePrepProfileMutation();
  /** A delete the server refused: the profile is still saved, and the user is told. */
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleDelete = async (p: PrepProfile) => {
    setDeleteError(null);
    const outcome = await mutationOutcome(remove(p.id), 'This preparation could not be deleted. It is still saved — try again.', setDeleteError);
    // Deselect only what was really deleted.
    if (outcome.ok && selectedId === p.id) onSelect(null);
  };
  if (isLoading) return <p className="text-sm text-gray-500">Loading your saved preparation…</p>;
  if (isError) return null;
  if (!profiles || profiles.length === 0) {
    return <p className="text-sm text-gray-500">You have no saved preparation yet. Save one after preparing notes.</p>;
  }
  return (
    <>
    {deleteError && (
      <p className="mb-2 text-sm text-red-600" role="alert" data-testid="prep-profile-delete-error">{deleteError}</p>
    )}
    <ul className="space-y-2" data-testid="prep-profiles">
      {profiles.map((p) => (
        <li key={p.id} className={`rounded-lg border p-3 ${selectedId === p.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between gap-2">
            <button type="button" className="text-left text-sm font-medium text-gray-900" onClick={() => onSelect(selectedId === p.id ? null : p)}>
              {p.label}
              {p.context.roleTitle && <span className="ml-2 text-xs font-normal text-gray-500">{p.context.roleTitle.value}</span>}
            </button>
            <button
              type="button"
              disabled={removeState.isLoading}
              onClick={() => void handleDelete(p)}
              className="text-xs text-gray-500 hover:text-red-700"
            >
              Delete
            </button>
          </div>
          {selectedId === p.id && (
            <div className="mt-2">
              <ProfileHistory id={p.id} />
            </div>
          )}
        </li>
      ))}
    </ul>
    </>
  );
}

export default PrepProfilesPanel;
